/**
 * Framework display/formatting utilities (names, logos, enrolled-set labels).
 *
 * Used by both React components and non-React contexts like route beforeLoad
 * functions. Note: there is no "active framework" — workspaces are
 * multi-framework; format the full enrolled set via formatEnrolledFrameworksLabel.
 */

import {
  FRAMEWORK_DISPLAY_NAMES,
  FRAMEWORK_ENROLLMENT_FLAGS,
  getFrameworkDisplayName,
} from '@deepguide-ai/types'

// Re-export from shared types
export { FRAMEWORK_DISPLAY_NAMES, getFrameworkDisplayName }

/**
 * Resolve a public framework badge URL from a framework key, slug, or
 * free-text name. Input is normalized (lower-cased, non-alphanumerics
 * stripped) so 'SOC2' / 'soc-2' / 'SOC 2 Type II' / 'ISO_27001' all land on
 * the same asset. Returns `null` when nothing matches, so callers can fall
 * back to a generic icon.
 *
 * THE single resolver. Two used to exist — an exact-slug `Set` here and a
 * prefix matcher in `lib/framework-logo.ts` — and they disagreed: the Set
 * returned null for ISO 42001 (no badge rendered at all on the Audits page)
 * while the prefix matcher mapped every `iso*` key onto the ISO 27001 badge,
 * so the trust center would have shown the WRONG mark for the same framework.
 * Matching is prefix-based (the permissive behavior, which free-text trust
 * center rows need) but the old catch-all `iso` rule is gone: it badged any
 * future ISO standard as 27001.
 *
 * Order matters — most specific first.
 */
const FRAMEWORK_LOGO_MATCHERS: { test: (k: string) => boolean; file: string }[] = [
  { test: (k) => k.startsWith('soc'), file: 'soc2' },
  { test: (k) => k.startsWith('iso42'), file: 'iso42001' },
  { test: (k) => k.startsWith('iso27'), file: 'iso27001' },
  { test: (k) => k.includes('hipaa'), file: 'hipaa' },
  { test: (k) => k.includes('gdpr'), file: 'gdpr' },
  { test: (k) => k.includes('hitrust'), file: 'hitrust' },
  { test: (k) => k.startsWith('pci'), file: 'pci' },
  { test: (k) => k.includes('nist'), file: 'nist' },
  { test: (k) => k.includes('dora'), file: 'dora' },
]

export function getFrameworkLogoUrl(framework: string | null | undefined): string | null {
  if (!framework) return null
  const key = framework.toLowerCase().replace(/[^a-z0-9]/g, '')
  const match = FRAMEWORK_LOGO_MATCHERS.find((m) => m.test(key))
  return match ? `/frameworks/${match.file}.svg` : null
}

/**
 * Format an enrolled-frameworks list as a human-readable label.
 * - []           → "compliance"
 * - [SOC2]       → "SOC 2"
 * - [SOC2,HIPAA] → "SOC 2 and HIPAA"
 * - [A,B,C]      → "SOC 2, HIPAA, and ISO 27001"
 *
 * Used by register-style pages (Vendors, Readiness, Control Matrix) whose
 * data crosses framework boundaries — hardcoding a single framework label
 * on those pages misleads dual-enrolled workspaces.
 */
export function formatEnrolledFrameworksLabel(frameworks: string[]): string {
  if (frameworks.length === 0) return 'compliance'
  const names = frameworks.map((f) => getFrameworkDisplayName(f))
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

/**
 * Enrollable frameworks for pickers (create-workspace form, Setup guide).
 * Flag-gated frameworks (`FRAMEWORK_ENROLLMENT_FLAGS`) appear only when the
 * caller reports their flag as enabled.
 */
export function frameworkOptions(enabledFlags: Record<string, boolean>) {
  const catalog: Array<{ value: string; label: string; description: string }> = [
    { value: 'SOC2', label: 'SOC 2', description: 'AICPA trust services criteria, the standard for US B2B SaaS.' },
    { value: 'HIPAA', label: 'HIPAA', description: 'Required if you handle protected health information (PHI). Common for healthtech B2B.' },
    { value: 'ISO27001', label: 'ISO 27001', description: 'International information-security management standard, commonly asked for by EU and enterprise buyers.' },
    { value: 'ISO42001', label: 'ISO 42001', description: 'AI management system (AIMS) for companies building or deploying AI features.' },
    { value: 'GDPR', label: 'GDPR', description: 'EU data-protection readiness (self-assessment) for companies with EU customers or users.' },
  ]
  return [
    ...catalog.filter((option) => {
      const flag = FRAMEWORK_ENROLLMENT_FLAGS[option.value]
      return !flag || enabledFlags[flag] === true
    }),
    { value: 'OTHER', label: 'Other', description: 'PCI DSS, FedRAMP, or another framework.' },
  ]
}
